import { logDebug, logError } from '@ptek/client-logger';
import { getAuthHeaders } from '../config/api';
import { shouldUseNewService } from './apiPatternMatching';
import { fetchWithAuth } from './fetchWithAuth';

/**
 * Configuration for API endpoint routing
 * Controls which endpoints go to legacy vs ambient scribe REST service
 */
export interface ApiRouterConfig {
  /** Base URL for the legacy service */
  legacyBaseUrl: string;
  /** Base URL for the ambient scribe REST service (used for migrated endpoints) */
  ambientScribeRestUrl: string;
  /**
   * Whitelist of endpoint paths that should route to the ambient scribe REST service
   * All other endpoints will go to the legacy service
   * Supports method-specific patterns: 'METHOD:/path' or just '/path' (applies to all methods)
   * Supports path parameters: '/api/v1/sessions/{id}'
   * Supports wildcard '*' to route ALL endpoints to ambient scribe REST service
   * Examples: exact endpoints, method-specific patterns, path parameters
   */
  newServiceEndpoints: string[];
  /** Enable/disable migration routing entirely */
  migrationEnabled?: boolean;
}

/**
 * API Router that intelligently routes requests between legacy and ambient scribe REST services
 * based on endpoint whitelist configuration
 */
export class ApiRouter {
  private config: ApiRouterConfig;

  constructor(config: ApiRouterConfig) {
    this.config = config;
  }

  /**
   * Transform endpoint path for the target service
   * Legacy service expects paths as-is (/api/v1/templates)
   * Ambient scribe REST service expects paths without /api prefix (/v1/templates)
   */
  private transformEndpointPath(
    endpoint: string,
    useNewService: boolean
  ): string {
    if (!useNewService) {
      // Legacy service expects paths as-is
      return endpoint;
    }

    // Ambient scribe REST service expects paths without /api prefix
    if (endpoint.startsWith('/api/')) {
      const transformedPath = endpoint.replace('/api/', '/');
      logDebug('ApiRouter', `Path transform: ${endpoint} → ${transformedPath}`);
      return transformedPath;
    }

    return endpoint;
  }

  /**
   * Make an API call that automatically routes to the correct service
   */
  async makeApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<unknown> {
    const method = (options.method || 'GET').toUpperCase();

    const migrationEnabled = this.config.migrationEnabled || false;
    const useNewService =
      migrationEnabled &&
      shouldUseNewService(endpoint, method, this.config.newServiceEndpoints);

    const baseUrl = useNewService
      ? this.config.ambientScribeRestUrl
      : this.config.legacyBaseUrl;
    const transformedEndpoint = this.transformEndpointPath(
      endpoint,
      useNewService
    );
    const url = `${baseUrl}${transformedEndpoint}`;

    const serviceType = migrationEnabled
      ? useNewService
        ? 'AMBIENT_SCRIBE_REST'
        : 'LEGACY'
      : 'LEGACY (migration disabled)';
    logDebug(
      'ApiRouter',
      `${method} ${endpoint} → ${serviceType} service (${url})`
    );

    const headers = {
      ...(await getAuthHeaders()),
      ...(options.headers as Record<string, string>),
    };

    try {
      const response = await fetchWithAuth(url, { ...options, headers });

      if (!response.ok) {
        let errorText = 'Unknown error';
        try {
          errorText = await response.text();
        } catch {
          // Ignore error reading response text
        }

        const apiError = new Error(
          `API call failed: ${response.status} ${response.statusText} - ${errorText}`
        ) as Error & { status: number; responseBody: string };
        apiError.name = 'ApiHttpError';
        apiError.status = response.status;
        apiError.responseBody = errorText;
        throw apiError;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      } else {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch {
          return { success: true, data: text };
        }
      }
    } catch (error) {
      logError('ApiRouter', `Error making API call to ${url}:`, error);
      throw error;
    }
  }

  /**
   * Update the configuration (useful for testing different migration scenarios)
   */
  updateConfig(newConfig: Partial<ApiRouterConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current routing configuration (useful for debugging)
   */
  getConfig(): ApiRouterConfig {
    return { ...this.config };
  }

  /**
   * Get routing status for all configured endpoints (useful for debugging)
   */
  getRoutingStatus(): {
    endpoint: string;
    service: 'legacy' | 'ambient_scribe_rest';
  }[] {
    return this.config.newServiceEndpoints.map((endpoint) => ({
      endpoint,
      service: 'ambient_scribe_rest' as const,
    }));
  }
}

/**
 * Helper function to create a router with common configuration
 */
export function createApiRouter(
  legacyBaseUrl: string,
  ambientScribeRestUrl: string,
  newServiceEndpoints: string[] = [],
  migrationEnabled: boolean = true
): ApiRouter {
  return new ApiRouter({
    legacyBaseUrl,
    ambientScribeRestUrl,
    newServiceEndpoints,
    migrationEnabled,
  });
}
